import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

/**
 * The loader set (dev/design/GetVibed Loader.dc.html).
 *
 * ## Why there are several instead of one spinner
 *
 * A loading state is the interface telling you what it is doing. One generic
 * spinner says "wait" and nothing else, so every wait on the site feels
 * identical - the 200ms one and the 90-second transcode alike. These say which
 * kind of wait it is: bars for a page, a ring for a buffering track, a scanning
 * waveform for an upload being analysed, skeletons for a list.
 *
 * That is not decoration. Somebody watching a waveform scan while their upload
 * is analysed knows the site is working on *their file*, and is measurably more
 * willing to wait than somebody watching a circle turn.
 *
 * ## The rules, from the design, applied here rather than per call site
 *
 * - **1.05s bar cycle, 0.11s stagger, left to right.** Both live in `BARS`
 *   below, so every set of bars on the site is in step with every other.
 * - **Nothing under 400ms.** See `Deferred`. This is the rule that matters most
 *   and the one that is easiest to skip.
 * - **Lists get skeletons, never spinners.** A spinner in a list collapses the
 *   layout and then shoves it back open; a skeleton holds the space.
 * - **Reduced motion holds the bars at 60% and breathes the opacity.** Handled
 *   in `index.css`, keyed off the `data-gv-loader` / `data-gv-bar` attributes
 *   these components set - which is why those attributes are not optional.
 *
 * ## Everything here is decorative to assistive technology
 *
 * `aria-hidden` throughout, with the *state* announced by the thing that is
 * loading: `aria-busy` on the control, `role="status"` on the region. A spinner
 * that announces itself gets read out alongside the label that already said
 * "Loading…", so the message arrives twice.
 */

/** The one timing definition. Everything with bars reads these. */
const BARS = {
  duration: '1.05s',
  stagger: 0.11,
} as const;

/* -------------------------------------------------------------------------- */
/* The 400ms rule                                                              */
/* -------------------------------------------------------------------------- */

/**
 * True once `delay` has passed, so a fast response never flashes a loader.
 *
 * ## Why a flash is worse than nothing
 *
 * A spinner that appears for 80ms and vanishes is perceived as a glitch, not as
 * progress - the eye catches the movement without resolving what it was. It
 * also makes a fast site feel *less* reliable than a silent one, which is the
 * opposite of what the loader was added for.
 *
 * 400ms is the design's number and roughly where a delay stops being felt as
 * instant.
 *
 * The timer is cleared on unmount, so a component that resolves and disappears
 * inside the window never schedules a state update into a dead tree.
 */
export function useDelayed(active: boolean, delay = 400): boolean {
  const [shown, setShown] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!active) {
      setShown(false);

      return;
    }

    timer.current = setTimeout(() => setShown(true), delay);

    return () => {
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, [active, delay]);

  return shown;
}

/**
 * Render children only once the wait has gone on long enough to be worth
 * mentioning.
 *
 * The wrapper form, for the common case where the whole loading branch should
 * be withheld:
 *
 *     {isPending ? <Deferred><Loader /></Deferred> : <List items={data} />}
 *
 * Note this renders *nothing* for the first 400ms, so it must not be used where
 * something has to hold the layout open. Skeletons are the answer there, and
 * they are deliberately not wrapped in this.
 */
export function Deferred({ children, delay = 400 }: { children: ReactNode; delay?: number }) {
  return useDelayed(true, delay) ? <>{children}</> : null;
}

/* -------------------------------------------------------------------------- */
/* Bars                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The house colours, in order, left to right.
 *
 * Indigo through to green - the brand's own range rather than a spectrum. The
 * gradient across five bars is what makes this read as the GetVibed loader and
 * not a generic equaliser.
 */
const SPECTRUM = ['var(--color-signal)', 'var(--color-violet)', 'var(--color-hot)', 'var(--color-teal)', 'var(--color-fresh)'];

/** A row of equaliser bars, the workhorse of the set. */
function Bars({
  count = 5,
  width,
  className = '',
  colors,
  gap,
  soft = false,
}: {
  count?: number;
  width: number;
  className?: string;
  colors?: string[];
  gap: string;
  /** The quieter cycle, for bars inside something that is already moving. */
  soft?: boolean;
}) {
  const palette = colors ?? SPECTRUM;

  return (
    <div className={`flex items-center justify-center ${gap} ${className}`}>
      {Array.from({ length: count }, (_, i) => (
        <span
          key={i}
          data-gv-bar
          className="h-full rounded-[var(--radius-pill)]"
          style={{
            width: `${width}px`,
            // Cycled rather than clamped, so a three-bar set still spans the
            // range instead of using only the first three colours.
            background: palette[i % palette.length],
            transformOrigin: 'center',
            animation: `${soft ? 'gv-bar-soft' : 'gv-bar'} ${BARS.duration} cubic-bezier(0.42,0,0.58,1) ${
              i * BARS.stagger
            }s infinite`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Spectrum — page and route loads.
 *
 * The default. If you are not sure which loader a wait wants, it wants this.
 */
export function Loader({ size = 'md', className = '' }: { size?: 'sm' | 'md' | 'lg'; className?: string }) {
  const spec = {
    sm: { height: 'h-6', width: 3, gap: 'gap-[3px]' },
    md: { height: 'h-10', width: 5, gap: 'gap-1' },
    lg: { height: 'h-14', width: 7, gap: 'gap-1.5' },
  }[size];

  return (
    <span
      data-gv-loader="spectrum"
      aria-hidden="true"
      className={`inline-flex ${spec.height} items-center ${className}`}
    >
      <Bars width={spec.width} gap={spec.gap} className="h-full" />
    </span>
  );
}

/**
 * Bars sized for a button label.
 *
 * Inherits `currentColor` rather than taking the spectrum: inside a filled
 * button the label is near-black on indigo, and five brand colours in there
 * would be a second, competing palette in a 13px space.
 */
export function ButtonBars() {
  return (
    <span data-gv-loader="button" aria-hidden="true" className="flex h-[13px] items-center gap-[2.5px]">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          data-gv-bar
          className="h-full w-[2.5px] rounded-[var(--radius-pill)] bg-current"
          style={{
            transformOrigin: 'center',
            animation: `gv-bar 0.8s ease-in-out ${i * 0.1}s infinite`,
          }}
        />
      ))}
    </span>
  );
}

/**
 * Buffer ring — a track waiting on audio.
 *
 * Reserved for playback. A ring means "the sound is coming"; using it for a
 * page load would spend the association on the wrong thing, and then somebody
 * seeing it over the dock would not know whether their track had stalled.
 */
export function BufferRing({ size = 64, className = '' }: { size?: number; className?: string }) {
  return (
    <span
      data-gv-loader="ring"
      aria-hidden="true"
      className={`relative inline-block ${className}`}
      style={{ width: size, height: size }}
    >
      <span className="absolute inset-0 rounded-[var(--radius-pill)] border-[3px] border-ring" />
      <span
        data-gv-still
        className="absolute inset-0 rounded-[var(--radius-pill)] border-[3px] border-transparent"
        style={{
          borderTopColor: 'var(--color-signal)',
          borderRightColor: 'var(--color-hot)',
          animation: 'gv-spin 0.95s cubic-bezier(0.5,0.1,0.4,0.9) infinite',
        }}
      />
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-1/4 items-center gap-[2.5px]">
          {['var(--color-signal)', 'var(--color-text)', 'var(--color-fresh)'].map((color, i) => (
            <span
              key={color}
              data-gv-bar
              className="h-full w-[3px] rounded-[var(--radius-pill)]"
              style={{
                background: color,
                transformOrigin: 'center',
                animation: `gv-bar-soft 1.1s ease-in-out ${i * 0.14}s infinite`,
              }}
            />
          ))}
        </span>
      </span>
    </span>
  );
}

/**
 * Discovery pulse — finding artists, computing charts.
 *
 * Green and symmetrical, rising from the baseline rather than centred. Used
 * where the site is looking for something on your behalf, which is a different
 * promise from "this page is loading".
 */
export function DiscoveryPulse({ className = '' }: { className?: string }) {
  const heights = [22, 40, 58, 40, 22];
  const colors = ['var(--color-fresh-deep)', 'var(--color-fresh-mid)', 'var(--color-fresh)', 'var(--color-fresh-mid)', 'var(--color-fresh-deep)'];

  return (
    <span
      data-gv-loader="pulse"
      aria-hidden="true"
      className={`inline-flex items-end justify-center gap-[5px] ${className}`}
    >
      {heights.map((height, i) => (
        <span
          key={i}
          data-gv-bar
          className="w-2 rounded-[var(--radius-pill)]"
          style={{
            height,
            background: colors[i],
            // Bottom, not centre: these sit on a baseline, and a centred origin
            // would make them grow downward through it.
            transformOrigin: 'bottom',
            animation: `gv-bar-soft 1.3s ease-in-out ${i * 0.15}s infinite`,
          }}
        />
      ))}
    </span>
  );
}

/**
 * Going live — a room connecting.
 *
 * Hot red, and reserved for broadcast exactly as the palette intends. Nothing
 * uses it yet; rooms are Phase 7. It is here because the loader set was
 * designed as a set, and the alternative is somebody reaching for the ring when
 * rooms arrive and spending the live-red association on a spinner.
 */
export function GoingLive({ className = '' }: { className?: string }) {
  return (
    <span
      data-gv-loader="live"
      aria-hidden="true"
      className={`relative inline-flex h-16 w-16 items-center justify-center ${className}`}
    >
      {[0, 1].map((i) => (
        <span
          key={i}
          data-gv-still
          className="absolute h-16 w-16 rounded-[var(--radius-pill)] border border-hot"
          style={{ animation: `gv-ring 2s ease-out ${i}s infinite` }}
        />
      ))}
      <span
        data-gv-still
        className="relative flex h-8 w-8 items-center justify-center gap-[2px] rounded-[var(--radius-pill)] bg-hot"
        style={{ animation: 'gv-pulse 1.4s ease-in-out infinite' }}
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            data-gv-bar
            className="h-[13px] w-[2.5px] rounded-[var(--radius-pill)] bg-hot-wash"
            style={{
              transformOrigin: 'center',
              animation: `gv-bar 0.85s ease-in-out ${i * 0.12}s infinite`,
            }}
          />
        ))}
      </span>
    </span>
  );
}

/**
 * Waveform scan — an upload being analysed.
 *
 * A lit segment travelling along the wave itself, drawn with `stroke-dasharray`
 * so it follows the peaks rather than sliding a rectangle across them. That is
 * the whole point: it looks like something is reading the audio, because that is
 * what is happening.
 */
export function WaveformScan({ className = '' }: { className?: string }) {
  // A fixed shape rather than a random one: it is the same file being analysed
  // for as long as the component is mounted, so a wave that reshuffled on every
  // render would be saying something untrue.
  const points =
    '0,24 8,18 16,30 24,12 32,34 40,20 48,26 56,10 64,36 72,22 80,28 88,14 96,32 104,18 112,26 120,20 128,30 136,16 144,24 152,22 160,24';

  return (
    <svg
      data-gv-loader="waveform"
      aria-hidden="true"
      viewBox="0 0 160 48"
      className={`h-12 w-full max-w-40 ${className}`}
    >
      <polyline points="0,24 160,24" fill="none" stroke="var(--color-track-dim)" strokeWidth="1" />
      <polyline points={points} fill="none" stroke="var(--color-ring-lit)" strokeWidth="2" strokeLinecap="round" />
      <polyline
        data-gv-still
        points={points}
        fill="none"
        stroke="var(--color-signal)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="70 295"
        style={{ animation: 'gv-tick 2.2s linear infinite' }}
      />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* Skeletons                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * A shimmering placeholder line.
 *
 * ## Why lists get these and not a spinner
 *
 * A spinner in place of a list means the page is one height while loading and
 * another once loaded, so everything below it jumps at the moment the data
 * lands - frequently under the reader's cursor. A skeleton is the same height
 * as the thing it stands for, so nothing moves.
 *
 * Deliberately **not** wrapped in `Deferred`. The 400ms rule exists to stop a
 * flash of *movement*; a skeleton's job is to hold the layout from the first
 * frame, and delaying it would reintroduce exactly the jump it prevents.
 */
export function SkeletonLine({ width = '100%', className = '' }: { width?: string; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`block h-[9px] rounded-[var(--radius-pill)] ${className}`}
      style={{
        width,
        background: 'linear-gradient(90deg,var(--color-shimmer) 8%,var(--color-shimmer-peak) 38%,var(--color-shimmer) 68%)',
        backgroundSize: '460px 100%',
        animation: 'gv-shimmer 1.5s linear infinite',
      }}
    />
  );
}

/** Three staggered lines: the "feed skeleton" from the design. */
export function SkeletonText({ lines = 3, className = '' }: { lines?: number; className?: string }) {
  // Uneven, and deterministic. Equal-length bars read as a progress meter;
  // random ones reshuffle on every render.
  const widths = ['78%', '100%', '56%', '88%', '64%'];

  return (
    <span className={`flex flex-col gap-[9px] ${className}`} aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <span
          key={i}
          className="block h-[9px] rounded-[var(--radius-pill)]"
          style={{
            width: widths[i % widths.length],
            background: 'linear-gradient(90deg,var(--color-shimmer) 8%,var(--color-shimmer-peak) 38%,var(--color-shimmer) 68%)',
            backgroundSize: '460px 100%',
            animation: `gv-shimmer 1.5s linear ${i * 0.12}s infinite`,
          }}
        />
      ))}
    </span>
  );
}

/**
 * A placeholder for one track row: artwork, title, artist.
 *
 * Matched to the real row's dimensions, because a skeleton that is the wrong
 * height does the one thing skeletons exist to prevent.
 */
export function SkeletonRow() {
  return (
    <div
      className="flex items-center gap-4 rounded-[var(--radius-card)] border border-line bg-surface p-4"
      aria-hidden="true"
    >
      <SkeletonBlock className="h-11 w-11 rounded-[var(--radius-control)]" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <SkeletonLine width="42%" />
        <SkeletonLine width="24%" />
      </div>
    </div>
  );
}

/** A placeholder for one card in the dashboard's grid. */
export function SkeletonCard() {
  return (
    <div className="flex flex-col gap-2.5" aria-hidden="true">
      <SkeletonBlock className="aspect-square w-full rounded-[var(--radius-card)]" />
      <SkeletonLine width="80%" />
      <SkeletonLine width="50%" />
    </div>
  );
}

/**
 * Any rectangle standing in for something that has not arrived.
 *
 * `style` is passed through because some placeholders need a dimension that is
 * computed rather than a class - a chat bubble whose width varies per row, for
 * instance. It is merged *under* the shimmer, so a caller can set a width
 * without accidentally replacing the animation that makes this a skeleton.
 */
export function SkeletonBlock({
  className = '',
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <span
      aria-hidden="true"
      className={`block ${className}`}
      style={{
        ...style,
        background: 'linear-gradient(90deg,var(--color-shimmer) 8%,var(--color-shimmer-peak) 38%,var(--color-shimmer) 68%)',
        backgroundSize: '460px 100%',
        animation: 'gv-shimmer 1.5s linear infinite',
      }}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Whole-region and whole-page                                                 */
/* -------------------------------------------------------------------------- */

/**
 * A loader with a label, for a panel that has nothing else in it yet.
 *
 * `role="status"` on the wrapper and `aria-hidden` on the animation, so the
 * message is announced once as text rather than twice.
 */
export function LoadingPanel({ label = 'Loading…', className = '' }: { label?: string; className?: string }) {
  return (
    <div role="status" className={`flex items-center justify-center gap-4 p-8 ${className}`}>
      <Loader size="sm" />
      <span className="font-mono text-xs uppercase tracking-[0.12em] text-faint">{label}</span>
    </div>
  );
}
